import { useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import { WorkOrderBaseState } from './workOrderBase';
import { notifyProduction } from '@/lib/notifications/notificationService';
import { TEMPLATE_WO_STARTED, TEMPLATE_WO_COMPLETED, TEMPLATE_WO_CANCELLED } from '@/lib/notifications/templateCodes';

export const useWorkOrderOperations = (baseState: WorkOrderBaseState) => {
  const {
    loading,
    setLoading,
    selectedWorkOrder,
    setSelectedWorkOrder,
    toast
  } = baseState;

  /**
   * Start Production for a Work Order
   */
  const startProduction = useCallback(async (id: UUID) => {
    try {
      setLoading(true);
      
      // Verify the work order is in Draft status
      const { data: workOrder, error: fetchError } = await supabase
        .from('work_orders')
        .select('status')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (!workOrder) {
        throw new Error('Work order not found');
      }
      
      if (workOrder.status !== 'Draft') {
        throw new Error('Only work orders in Draft status can be started');
      }
      
      // Update work order status and set actual start date
      const { data, error } = await supabase
        .from('work_orders')
        .update({ 
          status: 'In Progress', 
          actual_start_date: new Date().toISOString() 
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Check if there are any operations to start
      const { data: firstOperation, error: opsError } = await supabase
        .from('work_order_operations')
        .select('id')
        .eq('work_order_id', id)
        .eq('sequence_no', 1)
        .single();
      
      // If there's a first operation, set it to In Progress
      if (!opsError && firstOperation) {
        await supabase
          .from('work_order_operations')
          .update({ 
            status: 'In Progress',
            actual_start_time: new Date().toISOString()
          })
          .eq('id', firstOperation.id);
      }
      
      // Queue email notification for WO started
      notifyProduction(
        TEMPLATE_WO_STARTED,
        { wo_number: data?.wo_number || id, actual_start_date: new Date().toISOString() },
        'work_orders',
        id
      );

      toast({
        title: 'Production started',
        description: 'Work Order has been moved to In Progress.',
        variant: 'success',
      });

      // Update selectedWorkOrder state if it matches
      if (selectedWorkOrder?.id === id) {
        setSelectedWorkOrder(data);
      }

      return data;
    } catch (error: any) {
      toast({
        title: 'Error starting production',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, selectedWorkOrder, setSelectedWorkOrder, toast]);

  /**
   * Complete a Work Order
   */
  const completeWorkOrder = useCallback(async (id: UUID) => {
    try {
      setLoading(true);
      
      // Verify the work order is in In Progress status
      const { data: workOrder, error: fetchError } = await supabase
        .from('work_orders')
        .select('status')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      if (!workOrder) {
        throw new Error('Work order not found');
      }
      
      if (workOrder.status !== 'In Progress') {
        throw new Error('Only work orders in In Progress status can be completed');
      }
      
      // Check if all operations are completed
      const { data: operations, error: opsError } = await supabase
        .from('work_order_operations')
        .select('id, status, qc_required')
        .eq('work_order_id', id);

      if (opsError) throw opsError;

      const allOperationsCompleted = operations.every(op => op.status === 'Completed');
      if (!allOperationsCompleted) {
        throw new Error('Cannot complete work order until all operations are completed');
      }

      // Check if all materials have been issued (at least partially)
      const { data: materials, error: materialsError } = await supabase
        .from('work_order_materials')
        .select('id, required_quantity, issued_quantity, status')
        .eq('work_order_id', id);

      if (materialsError) throw materialsError;

      if (materials && materials.length > 0) {
        const unissuedMaterials = materials.filter(m =>
          m.status === 'Not Issued' || (m.issued_quantity || 0) === 0
        );

        if (unissuedMaterials.length > 0) {
          throw new Error(`Cannot complete work order: ${unissuedMaterials.length} material(s) have not been issued. Please issue all required materials before completing.`);
        }

        // Optional: Check for materials that are only partially issued
        const partiallyIssuedMaterials = materials.filter(m =>
          m.status === 'Partially Issued' ||
          ((m.issued_quantity || 0) > 0 && (m.issued_quantity || 0) < (m.required_quantity || 0))
        );

        if (partiallyIssuedMaterials.length > 0) {
          // Log a warning but allow completion with partial materials
          console.warn(`Warning: ${partiallyIssuedMaterials.length} material(s) are only partially issued.`);
        }
      }
      
      // Check if all QC checks have passed for operations that require QC
      const operationsRequiringQC = operations.filter(op => op.qc_required);
      if (operationsRequiringQC.length > 0) {
        const operationIds = operationsRequiringQC.map(op => op.id);
        
        const { data: qcChecks, error: qcError } = await supabase
          .from('work_order_qc')
          .select('work_order_operation_id, status')
          .in('work_order_operation_id', operationIds);
        
        if (qcError) throw qcError;
        
        // Check if each operation requiring QC has at least one passing QC check
        const operationsWithPassingQC = new Set();
        qcChecks.forEach(qc => {
          if (qc.status === 'Pass') {
            operationsWithPassingQC.add(qc.work_order_operation_id);
          }
        });
        
        if (operationsWithPassingQC.size < operationsRequiringQC.length) {
          throw new Error('Cannot complete work order until all QC checks are passed');
        }
      }
      
      // Update work order status and set actual end date
      const { data, error } = await supabase
        .from('work_orders')
        .update({ 
          status: 'Completed', 
          actual_end_date: new Date().toISOString() 
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Queue email notification for WO completed
      notifyProduction(
        TEMPLATE_WO_COMPLETED,
        {
          wo_number: data?.wo_number || id,
          actual_end_date: new Date().toISOString(),
          duration: 'N/A',
          qty_completed: data?.completed_qty || 0,
        },
        'work_orders',
        id
      );

      toast({
        title: 'Work Order completed',
        description: 'Work Order has been successfully completed.',
        variant: 'success',
      });
      
      if (selectedWorkOrder?.id === id) {
        setSelectedWorkOrder(data);
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error completing work order',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Cancel a Work Order
   */
  const cancelWorkOrder = useCallback(async (id: UUID) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('work_orders')
        .update({ status: 'Cancelled' })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Queue email notification for WO cancelled
      notifyProduction(
        TEMPLATE_WO_CANCELLED,
        { wo_number: data?.wo_number || id, reason: 'Cancelled by user' },
        'work_orders',
        id,
        'HIGH'
      );

      toast({
        title: 'Work Order cancelled',
        description: 'Work Order has been cancelled successfully.',
        variant: 'success',
      });
      
      if (selectedWorkOrder?.id === id) {
        setSelectedWorkOrder(data);
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error cancelling work order',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  return {
    loading,
    startProduction,
    completeWorkOrder,
    cancelWorkOrder
  };
};
